import { initializeApp, getApp, getApps } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import { getFirestore, type Firestore } from 'firebase/firestore'
import { environment } from '@/config/environment'

/**
 * Dual-app initialization, mirroring iOS:
 *
 *   - default app  → DATA project (`crawfish-budget-{env}`)
 *   - "iam" app    → AUTH project (`crawfish-iam-{env}`)
 *
 * Auth tokens are minted by the IAM app; the backend verifies against
 * the IAM project. All data reads/writes are against the data app.
 * Sharing the IAM realm is what gives a user the same uid across
 * fitness/budget/meetings — see budget/CLAUDE.md › Multi-App Layout.
 */
export const IAM_APP_NAME = 'iam'

function initDefaultApp() {
  if (getApps().some((a) => a.name === '[DEFAULT]')) return getApp()
  return initializeApp({
    apiKey: environment.firebase.apiKey,
    authDomain: environment.firebase.authDomain,
    projectId: environment.firebase.projectId,
    appId: environment.firebase.appId,
  })
}

function initIamApp() {
  if (getApps().some((a) => a.name === IAM_APP_NAME)) return getApp(IAM_APP_NAME)
  return initializeApp(
    {
      apiKey: environment.iam.apiKey,
      authDomain: environment.iam.authDomain,
      projectId: environment.iam.projectId,
      appId: environment.iam.appId,
    },
    IAM_APP_NAME,
  )
}

const defaultApp = initDefaultApp()
const iamApp = initIamApp()

/** Firestore in the DATA project (transactions, accounts, budgets…). */
export const firestore: Firestore = getFirestore(defaultApp)

/** Auth in the IAM project. NEVER call `getAuth()` without an app — the
 *  default would silently authenticate against the data project, which
 *  isn't what we want (and won't match iOS / backend verification). */
export const iamAuth: Auth = getAuth(iamApp)

/** Auth on the DATA project. We don't sign users in here directly — the
 *  AuthProvider mints a custom token via `/auth/data-token` and signs in
 *  with that, so Firestore rules see `request.auth.uid` matching the
 *  IAM uid. Mirrors the iOS flow. */
export const dataAuth: Auth = getAuth(defaultApp)
